import { useContent } from '../../contexts/ContentContext'
import CtaButton from '../ui/CtaButton'
import { ChevronDown } from 'lucide-react'
import { motion } from 'motion/react'
import {
  resolveSectionTone,
  sectionTextClasses,
  resolveImageOverlay,
  accentTextClass,
} from '../../utils/sectionTone'
import RichText from '../../utils/richText'

/**
 * FullCTASection — Elementor-style full-band template.
 *
 * contentKey → {
 *   headline?, subline?, body?, cta?,
 *   surface?: 'image' | 'beige' | 'blue',
 *   variant?: 'onDark' | 'onLight',
 *   backgroundUrl?, overlay?,
 *   heightPercent?: number,
 *   bodyBox?: boolean,        // wrap body in panel (glass or paper)
 *   bodyBoxVariant?: 'glass' | 'paper',  // paper = cream #EDE7DC @ 0.9 (default paper)
 *   bodyCardsHeadline?: string,
 *   bodyCards?: { title?, body }[],  // TopicCard-style grid under body
 *   bodyCardsColumns?: 2 | 3 | 4,    // default 2
 *   scrollCue?: boolean,      // animated chevron → next section
 *   scrollTarget?: string,    // hash selector (default: next section sibling)
 * }
 */
/** Body: string | { text } | { parts } — keeps archive accent spans inside paragraphs */
function normalizeBody(body) {
  if (!body) return []
  if (typeof body === 'string') return [{ parts: body }]
  if (!Array.isArray(body)) return []
  return body
    .map((p) => {
      if (typeof p === 'string') return { parts: p }
      if (p?.parts) return { parts: p.parts }
      if (p?.text) return { parts: p.accent ? [{ text: p.text, accent: p.accent }] : p.text }
      return null
    })
    .filter(Boolean)
}

function resolveHeightPercent(block) {
  const raw = block.heightPercent ?? block.viewportHeight ?? 100
  const n = Number(raw)
  if (!Number.isFinite(n)) return 100
  return Math.min(100, Math.max(40, Math.round(n)))
}

function scrollToNext(sectionEl, target) {
  if (target) {
    const el = document.querySelector(target)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
  }
  const next = sectionEl?.nextElementSibling
  if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export default function FullCTASection({ contentKey = 'fullCTA' }) {
  const content = useContent()
  const block = content[contentKey]
  if (!block) return null

  const headline = block.headline || block.title
  const subline = block.subline || block.subtitle
  const bodyParas = normalizeBody(block.body)
  const cta = typeof block.cta === 'string' ? block.cta : block.cta?.label
  const hasImage =
    Boolean(block.backgroundUrl) && block.surface !== 'beige' && block.surface !== 'blue'
  const tone = resolveSectionTone(block)
  const t = sectionTextClasses(tone)
  const heightPercent = resolveHeightPercent(block)
  const bodyBox = Boolean(block.bodyBox)
  // paper = cream translucent panel (readable on busy photos); glass = dark frosted
  const bodyBoxVariant =
    block.bodyBoxVariant === 'glass' || block.bodyBoxVariant === 'paper'
      ? block.bodyBoxVariant
      : 'paper'
  const bodyCards = Array.isArray(block.bodyCards) ? block.bodyCards.filter(Boolean) : []
  const bodyCardsHeadline = block.bodyCardsHeadline || block.cardsHeadline
  const bodyCardsColumns = [2, 3, 4].includes(block.bodyCardsColumns)
    ? block.bodyCardsColumns
    : 2
  const scrollCue = Boolean(block.scrollCue)
  const imageOverlay = resolveImageOverlay(block, tone)

  let surfaceClass = 'bg-color-bg-light'
  if (block.surface === 'blue' || (!hasImage && t.onDark && block.surface !== 'beige')) {
    surfaceClass = 'bg-color-primary'
  }

  const id = block.id || contentKey

  const multiTitle = (block.titleParts?.length || 0) > 1
  const headlineNode = block.titleParts?.length ? (
    <>
      {block.titleParts.map((part, i) => {
        const accentCls = accentTextClass(part.accent)
        const blockCls = part.block || multiTitle ? 'block' : ''
        return (
          <span
            key={i}
            className={[accentCls, blockCls, i > 0 && multiTitle ? 'mt-2 md:mt-3' : '']
              .filter(Boolean)
              .join(' ')}
          >
            {part.text}
            {!blockCls ? ' ' : null}
          </span>
        )
      })}
    </>
  ) : (
    headline
  )

  if (!headlineNode && !subline && !bodyParas.length && !bodyCards.length && !cta) return null

  // Paper box: brand cream #EDE7DC @ ~0.9 — inline rgba (custom Tailwind /opacity is unreliable)
  // Glass box: dark frosted panel for on-dark surfaces
  const isPaperBox = bodyBox && bodyBoxVariant === 'paper'
  const paperBoxStyle = isPaperBox
    ? {
        backgroundColor: 'rgba(237, 231, 220, 0.9)',
        borderColor: 'rgba(34, 65, 96, 0.12)',
      }
    : undefined
  const boxClass = isPaperBox
    ? 'rounded-2xl border px-6 py-6 md:px-8 md:py-8 text-left'
    : t.onDark
      ? 'rounded-2xl border border-divider-on-dark bg-divider-on-dark/90 px-6 py-6 md:px-8 md:py-8 text-left'
      : 'rounded-2xl border border-color-bg-light bg-color-card-overlay px-6 py-6 md:px-8 md:py-8 text-left'
  // Body ink inside paper is always light-surface (primary/body), even when section tone is onDark
  const bodyTextClass = isPaperBox ? 'typography-body-lg' : t.body
  // Wider than max-w-3xl so multi-para essays match page content grids
  const bodyBoxWidthClass = 'w-full max-w-5xl'
  const cardsColClass =
    bodyCardsColumns === 4
      ? 'md:grid-cols-4'
      : bodyCardsColumns === 3
        ? 'md:grid-cols-3'
        : 'md:grid-cols-2'
  // Same paper craft as ForWhom TopicCards (cream panel, dark ink)
  const cardStyle = {
    backgroundColor: 'rgba(237, 231, 220, 0.9)',
    borderColor: 'rgba(34, 65, 96, 0.12)',
  }

  const bodyBlock =
    bodyParas.length > 0 ? (
      bodyBox ? (
        <div
          className={`mt-8 ${bodyBoxWidthClass} mx-auto space-y-4 ${boxClass}`}
          style={paperBoxStyle}
          data-testid="full-cta-body-box"
          data-body-box-variant={bodyBoxVariant}
        >
          {bodyParas.map((item, i) => (
            <p key={i} className={`${bodyTextClass} leading-relaxed`}>
              <RichText parts={item.parts} />
            </p>
          ))}
        </div>
      ) : (
        <div className="mt-8 space-y-4 max-w-3xl mx-auto">
          {bodyParas.map((item, i) => (
            <p key={i} className={`${t.body} leading-relaxed`}>
              <RichText parts={item.parts} />
            </p>
          ))}
        </div>
      )
    ) : null

  const cardsBlock =
    bodyCards.length > 0 ? (
      <div className="mt-10 md:mt-12 w-full max-w-content mx-auto" data-testid="full-cta-body-cards">
        {bodyCardsHeadline ? (
          <h3
            className={`${t.sub} effect-drop-display leading-snug max-w-4xl mx-auto text-center mb-6 md:mb-8`}
          >
            {bodyCardsHeadline}
          </h3>
        ) : null}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${cardsColClass} card-grid-gap-sm md:card-grid-gap-md`}
          data-columns={bodyCardsColumns}
        >
          {bodyCards.map((card, i) => (
            <div
              key={i}
              className="rounded-2xl border px-5 py-5 md:px-6 md:py-6 text-left flex flex-col gap-2"
              style={cardStyle}
              data-testid="full-cta-body-card"
            >
              {card.title ? (
                <h4 className="typography-h4 text-color-primary">{card.title}</h4>
              ) : null}
              {card.body ? (
                <p className="typography-body leading-relaxed text-color-text">{card.body}</p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    ) : null

  return (
    <section
      id={id}
      data-section="FullCTASection"
      data-testid={`full-cta-${contentKey}`}
      data-tone={tone}
      data-height-percent={heightPercent}
      data-body-box={bodyBox ? 'true' : undefined}
      className={`relative snap-section flex flex-col items-center justify-center overflow-hidden ${
        hasImage ? '' : surfaceClass
      }`}
      style={{ minHeight: `${heightPercent}svh` }}
    >
      {hasImage ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${block.backgroundUrl})` }}
            aria-hidden="true"
          />
          <div
            className={`absolute inset-0 ${imageOverlay.className || ''}`}
            style={imageOverlay.style}
            aria-hidden="true"
            data-testid="full-cta-image-overlay"
          />
        </>
      ) : null}

      <div className="relative z-10 w-full max-w-content mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24 text-center flex-1 flex flex-col justify-center">
        {headlineNode ? (
          <h2 className={`${t.display} leading-tight max-w-4xl mx-auto`}>{headlineNode}</h2>
        ) : null}

        {subline ? (
          <p className={`mt-6 md:mt-8 ${t.sub} max-w-3xl mx-auto leading-snug`}>{subline}</p>
        ) : null}

        {bodyBlock}

        {cardsBlock}

        {cta ? (
          <div className="mt-10 flex justify-center">
            <CtaButton label={cta} variant="primary" onDark={t.onDark} className="py-4 px-10" />
          </div>
        ) : null}
      </div>

      {scrollCue ? (
        <div className="relative z-10 pb-6 md:pb-10 flex justify-center">
          <motion.button
            type="button"
            aria-label="Weiter zur nächsten Sektion"
            data-testid={`full-cta-scroll-cue-${contentKey}`}
            className="next-arrow-button-on-dark opacity-80 hover:opacity-100 transition-opacity"
            onClick={(e) => {
              const section = e.currentTarget.closest('section')
              scrollToNext(section, block.scrollTarget)
            }}
            animate={{ y: [0, 10, 0] }}
            transition={{
              duration: 1.4,
              repeat: Infinity,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <ChevronDown className="w-full h-full" strokeWidth={1.5} />
          </motion.button>
        </div>
      ) : null}
    </section>
  )
}
